import React, { useState, useRef, useEffect } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { X, MousePointer2, Move } from 'lucide-react';

// Connection types with color coding
export const CONNECTION_TYPES = {
  G: { label: 'Gängad', color: '#3b82f6', example: 'G50', hint: 'Gängad anslutning, t.ex. G50' },
  F: { label: 'Flänsad', color: '#8b5cf6', example: 'F100', hint: 'Flänsad anslutning, t.ex. F100' },
  S: { label: 'Svetsad', color: '#f97316', example: 'S80', hint: 'Svetsad anslutning, t.ex. S80' },
  V: { label: 'Varmvatten', color: '#ef4444', example: 'V150kW', hint: 'Varmvattenslinga + effekt, t.ex. V150kW' },
  E: { label: 'Elpatron', color: '#eab308', example: 'E9kW', hint: 'Elpatron + effekt, t.ex. E9kW' },
};

export const heightLabel = (yPct) => (yPct < 33 ? 'HÖGT' : yPct < 66 ? 'MEDEL' : 'LÅGT');

// SVG dimensions
const SIDE_W = 220;
const SIDE_H = 420;
const SIDE_PAD_TOP = 30;
const SIDE_PAD_BOTTOM = 30;
const SIDE_BODY_H = SIDE_H - SIDE_PAD_TOP - SIDE_PAD_BOTTOM;

const TOP_W = 240;
const TOP_H = 240;
const TOP_CX = TOP_W / 2;
const TOP_CY = TOP_H / 2;
const TOP_R = 90;

const InteractiveTankDesigner = ({ connections, setConnections }) => {
  const [editing, setEditing] = useState(null);
  const [type, setType] = useState('G');
  const [dn, setDn] = useState('');
  const [effect, setEffect] = useState('');
  const [draggingId, setDraggingId] = useState(null);
  const [dragView, setDragView] = useState(null);

  const sideRef = useRef(null);
  const topRef = useRef(null);

  // Translate yPercent (0–100) to SVG y position on side view body
  const yPctToSvg = (yPct) => SIDE_PAD_TOP + (yPct / 100) * SIDE_BODY_H;
  const svgYToPct = (svgY) => Math.max(0, Math.min(100, ((svgY - SIDE_PAD_TOP) / SIDE_BODY_H) * 100));

  // Convert angle (-180..180, 0=right, 90=down) to top svg position on circle
  const angleToTopXY = (angle) => {
    const rad = (angle * Math.PI) / 180;
    return { x: TOP_CX + TOP_R * Math.cos(rad), y: TOP_CY + TOP_R * Math.sin(rad) };
  };

  const eventToSvgPoint = (svgEl, evt) => {
    // svgEl can be a <g> element. Walk up to the owning <svg> for createSVGPoint.
    const ownerSvg = svgEl?.ownerSVGElement || svgEl;
    if (!ownerSvg || !ownerSvg.createSVGPoint) return { x: 0, y: 0 };
    const pt = ownerSvg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const matrix = (svgEl.getScreenCTM && svgEl.getScreenCTM()) || ownerSvg.getScreenCTM();
    if (!matrix) return { x: 0, y: 0 };
    const transformed = pt.matrixTransform(matrix.inverse());
    return { x: transformed.x, y: transformed.y };
  };

  // SIDE click → place new (yPercent only; angle defaults 0 for new)
  const handleSideClick = (e) => {
    if (draggingId) return;
    const { y } = eventToSvgPoint(sideRef.current, e);
    const yPct = svgYToPct(y);
    setEditing({
      yPercent: yPct,
      angle: editing?.angle ?? 0,
      mode: 'new',
    });
  };

  // TOP click → place new (angle only; yPercent defaults 50)
  const handleTopClick = (e) => {
    if (draggingId) return;
    const { x, y } = eventToSvgPoint(topRef.current, e);
    const dx = x - TOP_CX;
    const dy = y - TOP_CY;
    const angle = Math.round(Math.atan2(dy, dx) * 180 / Math.PI);
    setEditing({
      yPercent: editing?.yPercent ?? 50,
      angle,
      mode: 'new',
    });
  };

  // Drag handlers for existing markers
  const handlePointerDown = (id, view) => (e) => {
    e.stopPropagation();
    setDraggingId(id);
    setDragView(view);
  };

  useEffect(() => {
    if (!draggingId) return;
    const handleMove = (e) => {
      if (dragView === 'side' && sideRef.current) {
        const { y } = eventToSvgPoint(sideRef.current, e);
        const yPct = svgYToPct(y);
        setConnections((prev) => prev.map((c) => (c.id === draggingId ? { ...c, yPercent: yPct } : c)));
      } else if (dragView === 'top' && topRef.current) {
        const { x, y } = eventToSvgPoint(topRef.current, e);
        const dx = x - TOP_CX;
        const dy = y - TOP_CY;
        const angle = Math.round(Math.atan2(dy, dx) * 180 / Math.PI);
        setConnections((prev) => prev.map((c) => (c.id === draggingId ? { ...c, angle } : c)));
      }
    };
    const handleUp = () => {
      setDraggingId(null);
      setDragView(null);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [draggingId, dragView, setConnections]);

  const confirmAdd = () => {
    if (!dn.trim()) return;
    const needsEffect = type === 'V' || type === 'E';
    if (needsEffect && !effect.trim()) return;
    const label = needsEffect ? `${type}${dn}${effect ? ` ${effect}` : ''}` : `${type}${dn}`;
    setConnections([
      ...connections,
      {
        id: Date.now(),
        type,
        dn: dn.trim(),
        effect: effect.trim(),
        label,
        yPercent: editing.yPercent,
        angle: editing.angle,
        height: heightLabel(editing.yPercent),
      },
    ]);
    setEditing(null);
    setDn('');
    setEffect('');
  };

  const removeConnection = (id) => {
    setConnections(connections.filter((c) => c.id !== id));
  };

  return (
    <div data-testid="interactive-tank-designer">
      <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8">
        {/* Visual designer (combined SVG so it's also exportable) */}
        <div className="bg-white border border-stone-200 p-4">
          <div className="flex items-center gap-2 mb-3 text-xs text-stone-500">
            <MousePointer2 className="w-3 h-3" />
            <span>Klicka på sidvyn för att placera höjd, klicka på toppvyn för att placera vinkel.</span>
          </div>
          <svg
            id="tank-designer-svg"
            data-testid="tank-designer-svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`0 0 ${SIDE_W + TOP_W + 40} ${Math.max(SIDE_H, TOP_H + 80) + 40}`}
            style={{ width: '100%', maxWidth: 520, height: 'auto', userSelect: 'none' }}
          >
            <rect x={0} y={0} width={SIDE_W + TOP_W + 40} height={Math.max(SIDE_H, TOP_H + 80) + 40} fill="#fafaf9" />

            {/* SIDE VIEW */}
            <g ref={sideRef} onClick={handleSideClick} style={{ cursor: 'crosshair' }}>
              <text x={10} y={20} fontSize="14" fontWeight="600" fill="#1c1917">Sidvy (höjd)</text>
              {/* Tank body */}
              <rect
                x={20}
                y={SIDE_PAD_TOP}
                width={180}
                height={SIDE_BODY_H}
                rx={8}
                fill="#ffffff"
                stroke="#321a27"
                strokeWidth={2}
              />
              {/* Top dome */}
              <ellipse cx={110} cy={SIDE_PAD_TOP} rx={90} ry={20} fill="#ffffff" stroke="#321a27" strokeWidth={2} />
              {/* Bottom dome */}
              <ellipse cx={110} cy={SIDE_PAD_TOP + SIDE_BODY_H} rx={90} ry={20} fill="#f5f5f4" stroke="#321a27" strokeWidth={2} />
              {/* Height zones */}
              <line x1={20} x2={200} y1={SIDE_PAD_TOP + SIDE_BODY_H * 0.33} y2={SIDE_PAD_TOP + SIDE_BODY_H * 0.33} stroke="#e7e5e4" strokeDasharray="4 4" />
              <line x1={20} x2={200} y1={SIDE_PAD_TOP + SIDE_BODY_H * 0.66} y2={SIDE_PAD_TOP + SIDE_BODY_H * 0.66} stroke="#e7e5e4" strokeDasharray="4 4" />
              <text x={205} y={SIDE_PAD_TOP + SIDE_BODY_H * 0.16} fontSize="10" fill="#78716c">HÖGT</text>
              <text x={205} y={SIDE_PAD_TOP + SIDE_BODY_H * 0.5} fontSize="10" fill="#78716c">MEDEL</text>
              <text x={205} y={SIDE_PAD_TOP + SIDE_BODY_H * 0.84} fontSize="10" fill="#78716c">LÅGT</text>

              {/* Existing markers (side: y from yPercent, x at right edge of cylinder) */}
              {connections.map((c, i) => {
                const cy = yPctToSvg(c.yPercent);
                const cx = 200; // right side of cylinder
                const color = CONNECTION_TYPES[c.type]?.color || '#321a27';
                return (
                  <g
                    key={`side-${c.id}`}
                    onMouseDown={handlePointerDown(c.id, 'side')}
                    style={{ cursor: 'grab' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <line x1={185} y1={cy} x2={210} y2={cy} stroke={color} strokeWidth={3} />
                    <circle cx={cx + 12} cy={cy} r={11} fill={color} stroke="#ffffff" strokeWidth={2} />
                    <text x={cx + 12} y={cy + 4} fontSize="11" fontWeight="700" fill="#fff" textAnchor="middle">
                      {i + 1}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* TOP VIEW */}
            <g
              ref={topRef}
              transform={`translate(${SIDE_W + 40}, 0)`}
              onClick={handleTopClick}
              style={{ cursor: 'crosshair' }}
            >
              <text x={10} y={20} fontSize="14" fontWeight="600" fill="#1c1917">Toppvy (vinkel)</text>
              {/* Outer subtle ring (clickable area) */}
              <circle cx={TOP_CX} cy={TOP_CY + 30} r={TOP_R + 25} fill="transparent" />
              {/* Tank top */}
              <circle cx={TOP_CX} cy={TOP_CY + 30} r={TOP_R} fill="#ffffff" stroke="#321a27" strokeWidth={2} />
              <circle cx={TOP_CX} cy={TOP_CY + 30} r={TOP_R - 8} fill="none" stroke="#e7e5e4" strokeDasharray="3 3" />
              {/* Cardinal axes */}
              <line x1={TOP_CX - TOP_R} x2={TOP_CX + TOP_R} y1={TOP_CY + 30} y2={TOP_CY + 30} stroke="#e7e5e4" />
              <line x1={TOP_CX} x2={TOP_CX} y1={TOP_CY + 30 - TOP_R} y2={TOP_CY + 30 + TOP_R} stroke="#e7e5e4" />
              <text x={TOP_CX + TOP_R + 6} y={TOP_CY + 33} fontSize="10" fill="#78716c">0°</text>
              <text x={TOP_CX - 8} y={TOP_CY + 30 + TOP_R + 14} fontSize="10" fill="#78716c">90°</text>
              <text x={TOP_CX - TOP_R - 28} y={TOP_CY + 33} fontSize="10" fill="#78716c">180°</text>
              <text x={TOP_CX - 16} y={TOP_CY + 30 - TOP_R - 4} fontSize="10" fill="#78716c">-90°</text>

              {connections.map((c, i) => {
                const rad = (c.angle * Math.PI) / 180;
                const cx = TOP_CX + TOP_R * Math.cos(rad);
                const cy = TOP_CY + 30 + TOP_R * Math.sin(rad);
                const color = CONNECTION_TYPES[c.type]?.color || '#321a27';
                return (
                  <g
                    key={`top-${c.id}`}
                    onMouseDown={handlePointerDown(c.id, 'top')}
                    style={{ cursor: 'grab' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <circle cx={cx} cy={cy} r={11} fill={color} stroke="#ffffff" strokeWidth={2} />
                    <text x={cx} y={cy + 4} fontSize="11" fontWeight="700" fill="#fff" textAnchor="middle">
                      {i + 1}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* Legend */}
            <g transform={`translate(10, ${Math.max(SIDE_H, TOP_H + 80) + 8})`}>
              {Object.entries(CONNECTION_TYPES).map(([k, v], idx) => (
                <g key={k} transform={`translate(${idx * 90}, 0)`}>
                  <circle cx={8} cy={8} r={6} fill={v.color} />
                  <text x={20} y={12} fontSize="10" fill="#1c1917">{k} = {v.label}</text>
                </g>
              ))}
            </g>
          </svg>

          {connections.length > 0 && (
            <div className="mt-2 text-xs text-stone-500 flex items-center gap-1">
              <Move className="w-3 h-3" /> Dra en markör för att flytta den.
            </div>
          )}
        </div>

        {/* Connection list */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-stone-900">
              Anslutningar ({connections.length})
            </h3>
            {connections.length < 2 && (
              <span className="text-xs text-orange-700 bg-orange-100 px-2 py-1 rounded">
                Minst 2 krävs
              </span>
            )}
          </div>

          {connections.length === 0 ? (
            <div className="bg-stone-50 border border-dashed border-stone-300 p-8 text-center text-sm text-stone-500">
              Klicka på sidvyn eller toppvyn för att lägga till din första anslutning.
            </div>
          ) : (
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-2">
              {connections.map((c, i) => {
                const meta = CONNECTION_TYPES[c.type];
                return (
                  <div
                    key={c.id}
                    className="flex items-center justify-between bg-white border border-stone-200 p-3"
                    data-testid={`connection-row-${i}`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: meta?.color || '#321a27' }}
                      >
                        {i + 1}
                      </span>
                      <div>
                        <p className="font-medium text-stone-900 text-sm">
                          {c.label}{' '}
                          <span className="text-stone-400 text-xs">({meta?.label})</span>
                        </p>
                        <p className="text-xs text-stone-500">
                          Höjd: {c.height} ({c.yPercent.toFixed(0)}%) · Vinkel: {c.angle}°
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeConnection(c.id)}
                      className="text-red-600 text-xs hover:underline"
                      data-testid={`remove-connection-${i}`}
                    >
                      Ta bort
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Add-connection popup */}
      {editing && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setEditing(null)}
          />
          <div
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white border border-stone-200 shadow-2xl z-50 w-[min(480px,92vw)] p-6"
            data-testid="connection-popup"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-bold text-stone-900">Ny anslutning</h3>
                <p className="text-sm text-stone-500 mt-1">
                  Höjd: <strong>{heightLabel(editing.yPercent)}</strong> ({editing.yPercent.toFixed(0)}%) · Vinkel: <strong>{editing.angle}°</strong>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="text-stone-400 hover:text-stone-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <Label className="mb-2 block">Typ av anslutning</Label>
              <div className="grid grid-cols-5 gap-2">
                {Object.entries(CONNECTION_TYPES).map(([k, v]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setType(k)}
                    data-testid={`type-btn-${k}`}
                    className={`py-3 px-2 border-2 text-xs font-bold flex flex-col items-center gap-1 transition-all ${
                      type === k ? 'border-transparent text-white shadow-md' : 'border-stone-200 text-stone-600 hover:border-stone-400'
                    }`}
                    style={type === k ? { backgroundColor: v.color } : {}}
                  >
                    <span className="text-base">{k}</span>
                    <span className="text-[10px] font-medium opacity-90">{v.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-stone-500 mt-2">{CONNECTION_TYPES[type].hint}</p>
            </div>

            <div className="mb-4">
              <Label>Dimension (DN) *</Label>
              <Input
                placeholder={`t.ex. ${CONNECTION_TYPES[type].example.replace(type, '').replace(/[a-zA-Z]+/g, '')}`}
                value={dn}
                onChange={(e) => setDn(e.target.value)}
                autoFocus
                data-testid="popup-dn-input"
              />
            </div>

            {(type === 'V' || type === 'E') && (
              <div className="mb-4">
                <Label>Effekt *</Label>
                <Input
                  placeholder={type === 'V' ? 't.ex. 150kW' : 't.ex. 9kW'}
                  value={effect}
                  onChange={(e) => setEffect(e.target.value)}
                  data-testid="popup-effect-input"
                />
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditing(null)}
                className="flex-1 rounded-none"
              >
                Avbryt
              </Button>
              <Button
                type="button"
                onClick={confirmAdd}
                disabled={!dn.trim() || ((type === 'V' || type === 'E') && !effect.trim())}
                className="flex-1 bg-[#321a27] hover:bg-[#4a2d3d] text-white rounded-none"
                data-testid="popup-confirm-btn"
              >
                Lägg till
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Utility: convert the on-screen tank-designer-svg into a base64 PNG
export const captureTankDesignAsPng = async () => {
  const svg = document.getElementById('tank-designer-svg');
  if (!svg) return null;
  try {
    const clone = svg.cloneNode(true);
    // Inline the dimensions explicitly
    const vb = svg.viewBox.baseVal;
    const w = vb.width || svg.clientWidth || 600;
    const h = vb.height || svg.clientHeight || 500;
    clone.setAttribute('width', w);
    clone.setAttribute('height', h);
    const xml = new XMLSerializer().serializeToString(clone);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const dataUrl = `data:image/svg+xml;base64,${svg64}`;

    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const scale = 2;
        const canvas = document.createElement('canvas');
        canvas.width = w * scale;
        canvas.height = h * scale;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = (e) => reject(e);
      img.src = dataUrl;
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('PNG capture failed:', err);
    return null;
  }
};

export default InteractiveTankDesigner;
